
import React, { useState, useEffect, useRef } from 'react';
import { Page } from '@/types/page';
import { getHebrewDate } from '@/utils/pageUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Printer, Download } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { TitleConfig } from '@/types/TitleConfig';

interface PrintablePagesReportProps {
  pages: Page[];
  titleConfig?: TitleConfig;
}

const PrintablePagesReport: React.FC<PrintablePagesReportProps> = ({ pages, titleConfig }) => {
  const [currentHebrewDate, setCurrentHebrewDate] = useState<string>('');
  const printRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const loadHebrewDate = async () => {
      const date = await getHebrewDate(new Date());
      setCurrentHebrewDate(date);
    };
    
    loadHebrewDate();
  }, []);

  const handlePrint = useReactToPrint({
    documentTitle: 'Pages Report',
    // @ts-ignore - the library types might be wrong but this is the correct usage
    content: () => printRef.current,
  });

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    
    const canvas = await html2canvas(printRef.current, {
      scale: 2,
      useCORS: true,
      logging: false,
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save('pages-report.pdf');
  };

  // Sort pages by position
  const sortedPages = [...pages].sort((a, b) => {
    if (!a.position && !b.position) return 0;
    if (!a.position) return -1;
    if (!b.position) return 1;
    
    // First compare by position type (left, right, fullscreen)
    if (a.position !== b.position) {
      return a.position.localeCompare(b.position);
    }
    
    // Then compare by order within the same position
    return a.order - b.order;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">דוח דפים</h2>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            הדפס
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF}>
            <Download className="mr-2 h-4 w-4" />
            הורד PDF
          </Button>
        </div>
      </div>
      
      <div ref={printRef} className="p-6 bg-white">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold">דוח דפים</h1>
          <p className="text-gray-500">
            תאריך: {new Date().toLocaleDateString('he-IL')} | {currentHebrewDate}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sortedPages.map((page) => (
            <Card key={page.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold">{page.title}</h3>
                    <p className="text-sm text-gray-500">סוג: {page.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">
                      עמודה: {page.position || 'left'}
                    </p>
                    <p className="text-sm">
                      סדר: {page.order || 0}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="font-medium mb-2">תוכן:</h4>
                  <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                    {JSON.stringify(page.content, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrintablePagesReport;
